export class WorkflowGroupDomainService {
    static fromJSON(json) {
        return Object.assign(Object.assign({}, json), { 
            /**
             * We just get a list of numbers from the server, so turn these into WorkflowGroupConnectionPairs
             *
             */
            connections: json.connections.reduce((cons, next, i) => i % 2 === 0
                ? cons.concat({ start: next, end: json.connections[i + 1] })
                : cons, []), createdDate: new Date(json.createdDate), modifiedDate: new Date(json.modifiedDate) });
    }
    static create(rootNode) {
        // @ts-expect-error - TS2739 - Type '{ rootNode: string; nodes: WorkflowGroupNode[]; connections: never[]; }' is missing the following properties from type 'WorkflowGroup': id, createdDate, modifiedDate
        return {
            rootNode: rootNode.id,
            nodes: [rootNode],
            connections: [],
        };
    }
    static isWorkflowLinear(group) {
        return group.nodes.every((node) => 
        // A workflow group is linear if and only if each definition
        // has one and only one connection from and to it, except for the root node,
        // which just has one connection from it, and the last node, which only has one connection
        // to it.  Workflow groups with no nodes or only one node are considered linear
        (group.rootNode === node.id ||
            group.connections.filter((connection) => connection.end === node.id)
                .length === 1) &&
            // And there is one or less nodes coming out of it
            group.connections.filter((con) => con.start === node.id).length <= 1);
    }
    static getOrderedNodesInLinearWorkflow(group) {
        if (!WorkflowGroupDomainService.isWorkflowLinear(group)) {
            throw new Error("Workflow group is not linear");
        }
        if (!group.rootNode) {
            return [];
        }
        const rootNode = group.nodes.find((n) => n.id === group.rootNode);
        if (!rootNode) {
            return [];
        }
        // We need to start with the root node and find each node one by one
        const nodeList = [rootNode];
        let currentConnection = group.connections.find((con) => con.start === group.rootNode);
        while (currentConnection != null) {
            const definition = group.nodes.find(
            // eslint-disable-next-line no-loop-func
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            (node) => node.id === currentConnection.end);
            if (definition) {
                nodeList.push(definition);
            }
            currentConnection = group.connections.find(
            // eslint-disable-next-line no-loop-func
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            (nextCon) => nextCon.start === currentConnection.end);
        }
        return nodeList;
    }
    static findLastNodeInLinearWorkflow(group) {
        if (!WorkflowGroupDomainService.isWorkflowLinear(group)) {
            throw new Error("Workflow group is not linear");
        }
        // If the workflow is linear, then we just need to find the definition
        // that doesn't have a connection from it
        return group.nodes.find(({ id }) => !group.connections.some((con) => con.start === id));
    }
    static addNodeToEndOfLinearWorkflow(group, newNode) {
        const lastNode = WorkflowGroupDomainService.findLastNodeInLinearWorkflow(group);
        if (lastNode) {
            return Object.assign(Object.assign({}, group), { nodes: group.nodes.concat(newNode), connections: group.connections.concat({
                    start: lastNode.id,
                    end: newNode.id,
                }) });
        }
        // This workflow doesn't have any nodes, so this must be the root node
        return Object.assign(Object.assign({}, group), { rootNode: newNode.id, nodes: group.nodes.concat(newNode), connections: [] });
    }
    static addNodesToEndOfLinearWorkflow(group, newNodes) {
        return newNodes.reduce((newGroup, node) => WorkflowGroupDomainService.addNodeToEndOfLinearWorkflow(newGroup, node), group);
    }
    static removeNodeFromLinearWorkflow(group, nodeId) {
        if (!WorkflowGroupDomainService.isWorkflowLinear(group)) {
            throw new Error("Workflow group is not linear");
        }
        const workflowGroupNodes = group.nodes.filter((node) => node.id !== nodeId);
        const connectionBefore = group.connections.find((connection) => connection.end === nodeId);
        const connectionAfter = group.connections.find((connection) => connection.start === nodeId);
        let connections = group.connections.filter((connection) => connection.start !== nodeId && connection.end !== nodeId);
        if (connectionBefore && connectionAfter) {
            connections = connections.concat({
                start: connectionBefore.start,
                end: connectionAfter.end,
            });
        }
        return Object.assign(Object.assign({}, group), { nodes: workflowGroupNodes, connections });
    }
    static setNewOrderForNodesInLinearWorkflow(group, newNodeOrder) {
        if (newNodeOrder.length === 0) {
            return group;
        }
        // We need to clear out the connections and reset them.  We also need to reset the root node to whatever is now in front
        const rootNode = newNodeOrder[0];
        const connections = newNodeOrder
            .slice(0, newNodeOrder.length - 1)
            .map((node, i) => ({
            start: node,
            end: newNodeOrder[i + 1],
        }));
        return Object.assign(Object.assign({}, group), { rootNode,
            connections });
    }
    static serializeConnections(group) {
        return group.connections.flatMap(({ start, end }) => [start, end]);
    }
}
